import React, { useState } from 'react';
import { FriendLocation } from '../../../types';
import { useRoute } from '../../../contexts/RouteContext';

interface MapSearchBarProps {
  showSearchBar: boolean;
  setShowSearchBar: (show: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearchChange: (value: string) => void;
  isSearching: boolean;
  friendResults: FriendLocation[];
  placeResults: any[];
  selectFriendFromSearch: (friend: FriendLocation) => void;
  selectPlaceFromSearch: (place: any) => void;
  getPhotoUrl: (photo?: string) => string | null;
  searchHistory: string[];
  onSelectHistoryItem: (query: string) => void;
  t: (key: string) => string;
  isPanelOpen?: boolean; // Whether any panel (friend or list) is open
  onClosePanel?: () => void; // Callback to close the current panel
  canGoBack?: boolean; // Whether there's navigation history
  onBack?: () => void; // Callback to go back in panel history
  friends?: FriendLocation[]; // Keep for compatibility
  onSearchPlace?: (query: string) => Promise<any[]>; // Keep for compatibility
}

/**
 * Unified search bar component for friends and places
 * Hidden when routing mode is active (RoutePanel handles routing inputs)
 */
export const MapSearchBar: React.FC<MapSearchBarProps> = ({
  showSearchBar,
  setShowSearchBar,
  searchQuery,
  setSearchQuery,
  handleSearchChange,
  isSearching,
  friendResults,
  placeResults,
  selectFriendFromSearch,
  selectPlaceFromSearch,
  getPhotoUrl,
  searchHistory,
  onSelectHistoryItem,
  t,
  isPanelOpen = false,
  onClosePanel,
  canGoBack = false,
  onBack,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [focusedByClick, setFocusedByClick] = useState(false); // Track if focus was from user click
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Only show history if user clicked on the input (not programmatic focus)
  const shouldShowHistory = isFocused && focusedByClick && searchQuery.trim() === '' && searchHistory.length > 0 && !isSearching;

  // Build a flat list of all selectable items for keyboard navigation
  const getAllItems = () => {
    const items: { type: 'history' | 'friend' | 'place'; data: any }[] = [];

    if (shouldShowHistory) {
      searchHistory.forEach((item) => items.push({ type: 'history', data: item }));
    } else {
      friendResults.forEach((friend) => items.push({ type: 'friend', data: friend }));
      placeResults.forEach((place) => items.push({ type: 'place', data: place }));
    }

    return items;
  };

  const allItems = getAllItems();

  // Reset selected index when results change
  React.useEffect(() => {
    setSelectedIndex(-1);
  }, [friendResults, placeResults, searchHistory, shouldShowHistory]);

  // Auto-focus input when search bar opens (without triggering history)
  React.useEffect(() => {
    if (showSearchBar && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
        // Don't set focusedByClick - let user click to show history
      }, 100);
    }
  }, [showSearchBar]);

  // Reset focusedByClick when search bar closes
  React.useEffect(() => {
    if (!showSearchBar) {
      setFocusedByClick(false);
    }
  }, [showSearchBar]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (allItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allItems.length) {
          const selectedItem = allItems[selectedIndex];
          if (selectedItem.type === 'history') {
            handleSelectHistory(selectedItem.data);
            return; // Don't blur for history - keep focus for editing
          } else if (selectedItem.type === 'friend') {
            const friend = selectedItem.data;
            setSearchQuery(`${friend.first_name} ${friend.last_name}`);
            selectFriendFromSearch(friend);
          } else if (selectedItem.type === 'place') {
            const place = selectedItem.data;
            setSearchQuery(place.display_name.split(',')[0]);
            selectPlaceFromSearch(place);
          }
          setSelectedIndex(-1);
          // Blur input to hide dropdown
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Helper to get item index in the flat list
  const getItemIndex = (type: 'history' | 'friend' | 'place', index: number): number => {
    if (shouldShowHistory) {
      return type === 'history' ? index : -1;
    }
    if (type === 'friend') return index;
    if (type === 'place') return friendResults.length + index;
    return -1;
  };

  // Hide search bar when routing mode is active
  const { isRoutingMode } = useRoute();

  const handleClosePanel = () => {
    if (onClosePanel) {
      onClosePanel();
      // Clear search and focus the input for a new search
      setSearchQuery('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleSelectHistory = (historyItem: string) => {
    onSelectHistoryItem(historyItem);
    // Focus the input and place cursor at the end
    // Delay must be > 200ms (onBlur delay) to ensure focus happens after blur
    setTimeout(() => {
      if (inputRef.current) {
        setFocusedByClick(true); // Keep as user interaction
        inputRef.current.focus();
        // Place cursor at the end
        const length = historyItem.length;
        inputRef.current.setSelectionRange(length, length);
      }
    }, 250);
  };

  // Hide completely when routing mode is active
  if (isRoutingMode) return null;

  return (
    <>
      {/* Search icon button for mobile */}
      <button
        className="search-toggle-button"
        onClick={() => {
          const newState = !showSearchBar;
          setShowSearchBar(newState);
          // Clear search when closing on mobile
          if (!newState) {
            setSearchQuery('');
          }
        }}
        title={t('map.search.title')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="search-icon-svg">
          <circle cx="10" cy="10" r="7"/>
          <line x1="15" y1="15" x2="22" y2="22"/>
        </svg>
      </button>

      {/* Main unified search bar - Google Maps style */}
      {showSearchBar && (
        <div className={`main-search-bar ${isPanelOpen ? 'with-panel-open' : ''} ${canGoBack ? 'with-back-button' : ''}`}>
          <div className="search-input-container">
            {/* Back button (left side) - only when there's navigation history */}
            {canGoBack && onBack && (
              <button
                className="search-back-button"
                onClick={onBack}
                title="Retour"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
              </button>
            )}

            {/* Separator after back button */}
            {canGoBack && <div className="search-separator" />}

            {/* Search input */}
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={t('map.search.placeholder')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onMouseDown={() => setFocusedByClick(true)} // User clicked - show history
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                // Delay to allow click on history items
                setTimeout(() => {
                  setIsFocused(false);
                  setFocusedByClick(false); // Reset click tracking
                  setSelectedIndex(-1);
                }, 200);
              }}
              onKeyDown={handleKeyDown}
            />

            {/* Right side buttons */}
            <div className="search-buttons">
              {/* Search/loading icon */}
              <button
                className="search-icon-button"
                disabled={isSearching}
              >
                {isSearching ? (
                  <span className="search-loading">⏳</span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="search-icon-svg">
                    <circle cx="10" cy="10" r="7"/>
                    <line x1="15" y1="15" x2="22" y2="22"/>
                  </svg>
                )}
              </button>

              {/* Close panel button - only when a panel is open */}
              {isPanelOpen && onClosePanel && (
                <button
                  className="search-close-button"
                  onClick={handleClosePanel}
                  title={t('map.search.closePanel') || 'Fermer'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Search history */}
          {shouldShowHistory && (
            <div className="search-results">
              <div className="results-section-header">🕒 Recherches récentes</div>
              {searchHistory.map((historyItem, index) => (
                <div
                  key={index}
                  className={`search-result-item ${getItemIndex('history', index) === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelectHistory(historyItem)}
                >
                  <div className="result-info">
                    <div className="result-name">{historyItem}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Unified search results - only show when focused */}
          {isFocused && (friendResults.length > 0 || placeResults.length > 0) && (
            <div className="search-results">
              {/* Friends results section */}
              {friendResults.length > 0 && (
                <>
                  <div className="results-section-header">👥 Amis</div>
                  {friendResults.map((friend: FriendLocation, index: number) => {
                    const photoUrl = getPhotoUrl(friend.profile_photo);
                    const initials = `${friend.first_name.charAt(0)}${friend.last_name.charAt(0)}`.toUpperCase();

                    return (
                      <div
                        key={friend.id}
                        className={`search-result-item ${getItemIndex('friend', index) === selectedIndex ? 'selected' : ''}`}
                        onClick={() => selectFriendFromSearch(friend)}
                      >
                        <div className="result-avatar">
                          {photoUrl ? (
                            <img src={photoUrl} alt={`${friend.first_name} ${friend.last_name}`} className="avatar-img" />
                          ) : (
                            <div className="avatar-initials">{initials}</div>
                          )}
                        </div>
                        <div className="result-info">
                          <div className="result-name">{friend.first_name} {friend.last_name}</div>
                          <div className="result-address">{friend.city}, {friend.country}</div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Places results section */}
              {placeResults.length > 0 && (
                <>
                  <div className="results-section-header">📍 Lieux</div>
                  {placeResults.map((place: any, index: number) => (
                    <div
                      key={index}
                      className={`search-result-item ${getItemIndex('place', index) === selectedIndex ? 'selected' : ''}`}
                      onClick={() => selectPlaceFromSearch(place)}
                    >
                      <div className="result-icon">📍</div>
                      <div className="result-info">
                        <div className="result-name">{place.display_name.split(',')[0]}</div>
                        <div className="result-address">{place.display_name}</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isFocused && searchQuery && friendResults.length === 0 && placeResults.length === 0 && !isSearching && (
            <div className="search-no-results">
              {t('map.search.noResults')}
            </div>
          )}
        </div>
      )}
    </>
  );
};
